import { useMemo, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import type { BreedingPair } from '../api/breeding';
import { getBirdDisplayId } from '../utils/birdDisplay';

export function breedingPairLabel(pair: BreedingPair): string {
  const male = pair.maleBirdEntity ? getBirdDisplayId(pair.maleBirdEntity) : 'Unknown male';
  const female = pair.femaleBirdEntity ? getBirdDisplayId(pair.femaleBirdEntity) : 'Unknown female';
  const cage = pair.cageNumber ? `Cage ${pair.cageNumber}` : 'No cage';
  const ended = pair.pairingEndDate ? ' · Ended' : '';
  return `${male} × ${female} (${cage})${ended}`;
}

function breedingPairSearchText(pair: BreedingPair): string {
  return [
    pair.cageNumber,
    pair.nestNumber,
    pair.comments,
    pair.maleBirdEntity ? getBirdDisplayId(pair.maleBirdEntity) : '',
    pair.femaleBirdEntity ? getBirdDisplayId(pair.femaleBirdEntity) : '',
    pair.maleBirdEntity?.species?.commonName,
    pair.femaleBirdEntity?.species?.commonName,
    pair.maleBirdEntity?.bandId,
    pair.femaleBirdEntity?.bandId,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchesQuery(pair: BreedingPair, query: string): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = breedingPairSearchText(pair);
  return tokens.every((token) => haystack.includes(token));
}

function sortBreedingPairs(list: BreedingPair[]): BreedingPair[] {
  return [...list].sort((a, b) => {
    const aEnded = Boolean(a.pairingEndDate);
    const bEnded = Boolean(b.pairingEndDate);
    if (aEnded !== bEnded) return aEnded ? 1 : -1;
    return breedingPairLabel(a).localeCompare(breedingPairLabel(b), undefined, {
      sensitivity: 'base',
    });
  });
}

interface BreedingPairComboboxProps {
  id?: string;
  pairs: BreedingPair[];
  value: string;
  onChange: (pairId: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  wrapperClassName?: string;
  optionsMaxHeightClass?: string;
  allowClear?: boolean;
}

export default function BreedingPairCombobox({
  id = 'breedingPairId',
  pairs,
  value,
  onChange,
  required = false,
  disabled = false,
  placeholder = 'Search breeding pairs…',
  emptyMessage = 'No breeding pairs available',
  wrapperClassName = 'mt-1',
  optionsMaxHeightClass = 'max-h-60',
  allowClear = false,
}: BreedingPairComboboxProps) {
  const [query, setQuery] = useState('');

  const sortedPairs = useMemo(() => sortBreedingPairs(pairs), [pairs]);

  const selected = useMemo(
    () => sortedPairs.find((pair) => pair.id === value) ?? null,
    [sortedPairs, value],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return sortedPairs;
    return sortedPairs.filter((pair) => matchesQuery(pair, query));
  }, [sortedPairs, query]);

  return (
    <Combobox
      as="div"
      value={selected}
      onChange={(next: BreedingPair | null) => {
        onChange(next?.id ?? '');
        setQuery('');
      }}
      disabled={disabled}
      nullable
    >
      {required ? (
        <input type="hidden" name={id} value={value} required />
      ) : null}
      <div className={`relative ${wrapperClassName}`}>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
        </div>
        <Combobox.Input
          id={id}
          className="w-full input border-gray-300 shadow-sm sm:text-sm pl-10 pr-10"
          displayValue={(pair: BreedingPair | null) => (pair ? breedingPairLabel(pair) : '')}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setQuery('')}
          placeholder={selected ? breedingPairLabel(selected) : placeholder}
          autoComplete="off"
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
        </Combobox.Button>

        {filtered.length > 0 ? (
          <Combobox.Options
            className={`absolute z-30 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm ${optionsMaxHeightClass}`}
          >
            {allowClear && value ? (
              <Combobox.Option
                value={null}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    active ? 'bg-cyan-600 text-white' : 'text-slate-500'
                  }`
                }
              >
                All pairs
              </Combobox.Option>
            ) : null}
            {filtered.map((pair) => (
              <Combobox.Option
                key={pair.id}
                value={pair}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    active ? 'bg-cyan-600 text-white' : 'text-slate-900'
                  }`
                }
              >
                {({ active, selected: isSelected }) => (
                  <>
                    <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                      {breedingPairLabel(pair)}
                    </span>
                    {(pair.maleBirdEntity?.species?.commonName ||
                      pair.femaleBirdEntity?.species?.commonName) && (
                      <span
                        className={`block truncate text-xs ${
                          active ? 'text-cyan-100' : 'text-slate-500'
                        }`}
                      >
                        {pair.maleBirdEntity?.species?.commonName ||
                          pair.femaleBirdEntity?.species?.commonName}
                        {pair.pairingEndDate ? ' · Pairing ended' : ''}
                      </span>
                    )}
                    {isSelected ? (
                      <span
                        className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                          active ? 'text-white' : 'text-cyan-600'
                        }`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        ) : query.trim() ? (
          <div className="absolute z-20 mt-1 w-full rounded-md bg-white px-3 py-2 text-sm text-slate-500 shadow-lg ring-1 ring-black/5">
            No breeding pairs match “{query.trim()}”
          </div>
        ) : sortedPairs.length === 0 ? (
          <div className="absolute z-20 mt-1 w-full rounded-md bg-white px-3 py-2 text-sm text-slate-500 shadow-lg ring-1 ring-black/5">
            {emptyMessage}
          </div>
        ) : null}
      </div>
    </Combobox>
  );
}
